import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, Building2, User, Mail, Lock, Eye, EyeOff,
  MapPin, Briefcase, Target, CreditCard, Sparkles,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function SignupFlow({ onLogin, onGoToSite }) {
  const [step, setStep] = useState(1); // 1=signup, 2=verify, 3=onboarding
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState('');
  const [trialEnds, setTrialEnds] = useState('');

  // Step 1: Signup form
  const [form, setForm] = useState({
    name: '', email: '', password: '', company_name: '',
    estimated_properties: 5, property_type: 'hotel',
  });

  // Step 3: Onboarding
  const [onboardStep, setOnboardStep] = useState(1);
  const [onboard, setOnboard] = useState({
    company_size: '', role_title: '', primary_goal: '',
    property_count: null, property_types: [],
    current_tools: '', billing_preference: '', billing_contact_email: '',
    interested_in_enterprise: false,
  });

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND}/api/auth/signup`, form);
      setToken(res.data.token);
      setTrialEnds(res.data.trial_ends);
      toast.success('Account created! Check your email to verify.');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  const skipToApp = () => {
    onLogin(token, 'admin');
  };

  const startOnboarding = () => setStep(3);

  const handleOnboardSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND}/api/auth/onboarding`, onboard, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Onboarding complete!');
      onLogin(token, 'admin');
    } catch { toast.error('Failed to save'); }
    finally { setLoading(false); }
  };

  const GOALS = [
    { id: 'visibility', label: 'See our full tech stack clearly', icon: '🗺️' },
    { id: 'compliance', label: 'Track compliance & governance', icon: '🛡️' },
    { id: 'cost_reduction', label: 'Reduce tech costs & waste', icon: '💰' },
    { id: 'standardization', label: 'Standardize across properties', icon: '📐' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <button onClick={onGoToSite} className="inline-flex items-center gap-2 mb-6">
            <img src="/assets/logo.png" alt="LodgiFlow" className="h-8 w-8" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>LodgiFlow</span>
          </button>
          {step === 1 && (
            <>
              <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Create your account</h1>
              <p className="text-sm text-muted-foreground">Get started with LodgiFlow for your portfolio.</p>
            </>
          )}
          {step === 2 && (
            <>
              <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Check your email</h1>
              <p className="text-sm text-muted-foreground">We sent a verification link to <strong>{form.email}</strong></p>
            </>
          )}
          {step === 3 && (
            <>
              <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Quick setup</h1>
              <p className="text-sm text-muted-foreground">Help us personalize your experience ({onboardStep}/3)</p>
            </>
          )}
        </div>

        {/* Step 1: Signup Form */}
        {step === 1 && (
          <form onSubmit={handleSignup} className="bg-white rounded-2xl border shadow-lg p-6 space-y-4" data-testid="signup-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Your Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Jane Smith" autoFocus data-testid="signup-name" />
              </div>
              <div>
                <Label className="text-xs">Company Name</Label>
                <Input value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Coastal Hospitality" data-testid="signup-company" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Work Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@coastalhospitality.com" data-testid="signup-email" />
            </div>
            <div>
              <Label className="text-xs">Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder="8+ characters" data-testid="signup-password" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Estimated Properties</Label>
                <Select value={String(form.estimated_properties)} onValueChange={v => setForm(p => ({ ...p, estimated_properties: parseInt(v) }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 5, 10, 15, 20, 30, 50, 100].map(n => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'property' : 'properties'}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Primary Property Type</Label>
                <Select value={form.property_type} onValueChange={v => setForm(p => ({ ...p, property_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hotel">Hotels</SelectItem>
                    <SelectItem value="full-service-restaurant">Full Service Restaurants</SelectItem>
                    <SelectItem value="qsr">Quick Service Restaurants</SelectItem>
                    <SelectItem value="fec">Family Entertainment Centers</SelectItem>
                    <SelectItem value="recreation">Recreation</SelectItem>
                    <SelectItem value="entertainment">Entertainment</SelectItem>
                    <SelectItem value="retail">Retail</SelectItem>
                    <SelectItem value="mixed">Mixed Portfolio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full gap-2 h-11" style={{ background: 'linear-gradient(to right, var(--wl-primary, #4F6DF5), var(--wl-accent, #6C5CE7))' }} disabled={loading || !form.name || !form.email || !form.password || !form.company_name} data-testid="signup-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Create Account
            </Button>
            <p className="text-[10px] text-center text-muted-foreground">By creating an account you agree to our Terms of Service</p>
          </form>
        )}

        {/* Step 2: Email Verification */}
        {step === 2 && (
          <div className="bg-white rounded-2xl border shadow-lg p-8 text-center space-y-6" data-testid="verify-step">
            <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
              <Mail className="h-8 w-8 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-4">Click the verification link in your email to confirm your account. You can start using LodgiFlow right away — just verify when you get a chance.</p>
              <Badge className="bg-blue-50 text-blue-700">Account created</Badge>
            </div>
            <div className="space-y-2">
              <Button onClick={startOnboarding} className="w-full gap-2 h-11" style={{ background: 'linear-gradient(to right, var(--wl-primary, #4F6DF5), var(--wl-accent, #6C5CE7))' }}>
                Continue to Setup <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={skipToApp} className="w-full text-xs text-muted-foreground">
                Skip — go straight to dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Onboarding Wizard */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border shadow-lg p-6 space-y-5" data-testid="onboarding-wizard">
            {/* Progress */}
            <div className="flex gap-1">
              {[1, 2, 3].map(s => <div key={s} className={`flex-1 h-1 rounded-full ${s > onboardStep ? 'bg-muted' : ''}`} style={s <= onboardStep ? { backgroundColor: 'var(--wl-primary, #4F6DF5)' } : undefined} />)}
            </div>

            {/* Onboard Step 1: About You */}
            {onboardStep === 1 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">About your organization</h3>
                <div>
                  <Label className="text-xs">Company Size</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[['small', '1-5 properties'], ['medium', '6-20 properties'], ['large', '21-50 properties'], ['enterprise', '50+ properties']].map(([id, label]) => (
                      <button key={id} onClick={() => setOnboard(p => ({ ...p, company_size: id }))}
                        className={`p-2.5 rounded-lg border text-xs text-left ${onboard.company_size !== id ? 'hover:bg-muted/60' : ''}`}
                        style={onboard.company_size === id ? { borderColor: 'var(--wl-primary, #4F6DF5)', backgroundColor: 'color-mix(in srgb, var(--wl-primary, #4F6DF5) 5%, transparent)' } : undefined}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Your Role</Label>
                  <Input value={onboard.role_title} onChange={e => setOnboard(p => ({ ...p, role_title: e.target.value }))} placeholder="VP of Technology, IT Director, CTO..." />
                </div>
                <Button onClick={() => setOnboardStep(2)} className="w-full gap-2" disabled={!onboard.company_size}>Next <ArrowRight className="h-4 w-4" /></Button>
              </div>
            )}

            {/* Onboard Step 2: Goals */}
            {onboardStep === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">What's your primary goal?</h3>
                <div className="space-y-2">
                  {GOALS.map(g => (
                    <button key={g.id} onClick={() => setOnboard(p => ({ ...p, primary_goal: g.id }))}
                      className={`w-full p-3 rounded-lg border text-left text-sm flex items-center gap-3 ${onboard.primary_goal !== g.id ? 'hover:bg-muted/60' : ''}`}
                      style={onboard.primary_goal === g.id ? { borderColor: 'var(--wl-primary, #4F6DF5)', backgroundColor: 'color-mix(in srgb, var(--wl-primary, #4F6DF5) 5%, transparent)' } : undefined}>
                      <span className="text-lg">{g.icon}</span> {g.label}
                    </button>
                  ))}
                </div>
                <div>
                  <Label className="text-xs">What tools do you currently use to track your tech stack?</Label>
                  <Input value={onboard.current_tools} onChange={e => setOnboard(p => ({ ...p, current_tools: e.target.value }))} placeholder="Spreadsheets, nothing, custom tool..." />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOnboardStep(1)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
                  <Button onClick={() => setOnboardStep(3)} className="flex-1 gap-2" disabled={!onboard.primary_goal}>Next <ArrowRight className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* Onboard Step 3: Billing & Enterprise */}
            {onboardStep === 3 && (
              <div className="space-y-4">
                <h3 className="text-sm font-semibold">Billing preferences</h3>
                <p className="text-xs text-muted-foreground">This helps us prepare the right plan for your organization.</p>
                <div>
                  <Label className="text-xs">Preferred Billing</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[['credit_card', 'Credit Card'], ['invoice', 'Invoice / PO'], ['per_property', 'Per-Property Billing'], ['portfolio', 'Portfolio-Wide']].map(([id, label]) => (
                      <button key={id} onClick={() => setOnboard(p => ({ ...p, billing_preference: id }))}
                        className={`p-2.5 rounded-lg border text-xs text-left ${onboard.billing_preference !== id ? 'hover:bg-muted/60' : ''}`}
                        style={onboard.billing_preference === id ? { borderColor: 'var(--wl-primary, #4F6DF5)', backgroundColor: 'color-mix(in srgb, var(--wl-primary, #4F6DF5) 5%, transparent)' } : undefined}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Billing Contact Email (optional)</Label>
                  <Input value={onboard.billing_contact_email} onChange={e => setOnboard(p => ({ ...p, billing_contact_email: e.target.value }))} placeholder="accounting@company.com" />
                </div>
                <div>
                  <button onClick={() => setOnboard(p => ({ ...p, interested_in_enterprise: !p.interested_in_enterprise }))}
                    className={`w-full p-3 rounded-lg border text-left text-sm ${!onboard.interested_in_enterprise ? 'hover:bg-muted/60' : ''}`}
                    style={onboard.interested_in_enterprise ? { borderColor: 'var(--wl-primary, #4F6DF5)', backgroundColor: 'color-mix(in srgb, var(--wl-primary, #4F6DF5) 5%, transparent)' } : undefined}>
                    <span className="font-medium">Interested in Pro plan?</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">AI features, change management, budget planning, custom integrations, dedicated support</p>
                  </button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOnboardStep(2)} className="gap-1"><ArrowLeft className="h-4 w-4" /> Back</Button>
                  <Button onClick={handleOnboardSubmit} className="flex-1 gap-2" style={{ background: 'linear-gradient(to right, var(--wl-primary, #4F6DF5), var(--wl-accent, #6C5CE7))' }} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Launch Dashboard
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-[10px] text-muted-foreground mt-6">
          Already have an account? <button onClick={onGoToSite} className="hover:underline" style={{ color: 'var(--wl-primary, #4F6DF5)' }}>Log in</button>
        </p>
      </div>
    </div>
  );
}
